import { useEffect, useState, useCallback } from "react";
import { api } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2, Wind, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import NumericInput from "@/components/NumericInput";
import TempPresetButtons from "@/components/TempPresetButtons";

const STATUS_CONFIG = {
  complete:   { color: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, dot: "bg-emerald-500" },
  incomplete: { color: "bg-amber-50 text-amber-700 border-amber-200",       icon: Clock,         dot: "bg-amber-500"   },
  issue:      { color: "bg-red-50 text-red-700 border-red-200",             icon: AlertTriangle, dot: "bg-red-500"     },
};

const EMPTY_FORM = {
  product_name: "",
  date: new Date().toISOString().split("T")[0],
  entry_temperature: "",
  entry_time: "",
  exit_temperature: "",
  exit_time: "",
  notes: "",
};

export default function CoolingCell() {
  const { t } = useI18n();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterDate ? { date: filterDate } : {};
      const res = await api.get("/cooling", { params });
      setRecords(res.data || []);
    } catch {
      toast.error("Error al cargar los registros");
    } finally {
      setLoading(false);
    }
  }, [filterDate]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const openAdd = () => {
    setEditRecord(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (rec) => {
    setEditRecord(rec);
    setForm({
      product_name: rec.product_name || "",
      date: rec.date || new Date().toISOString().split("T")[0],
      entry_temperature: rec.entry_temperature ?? "",
      entry_time: rec.entry_time || "",
      exit_temperature: rec.exit_temperature ?? "",
      exit_time: rec.exit_time || "",
      notes: rec.notes || "",
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.product_name.trim() || !form.date || form.entry_temperature === "" || !form.entry_time) {
      toast.error("Completa los campos obligatorios");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        product_name: form.product_name.trim(),
        date: form.date,
        entry_temperature: parseFloat(form.entry_temperature),
        entry_time: form.entry_time,
        exit_temperature: form.exit_temperature !== "" ? parseFloat(form.exit_temperature) : null,
        exit_time: form.exit_time || null,
        notes: form.notes || "",
      };
      if (editRecord) {
        await api.put(`/cooling/${editRecord.cooling_id}`, payload);
        toast.success("Registro actualizado");
      } else {
        await api.post("/cooling", payload);
        toast.success("Entrada registrada");
      }
      setShowForm(false);
      fetchRecords();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/cooling/${deleteId}`);
      toast.success("Registro eliminado");
      setDeleteId(null);
      fetchRecords();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const displayed = filterStatus === "all"
    ? records
    : records.filter((r) => r.status === filterStatus);

  const counts = {
    complete:   records.filter((r) => r.status === "complete").length,
    incomplete: records.filter((r) => r.status === "incomplete").length,
    issue:      records.filter((r) => r.status === "issue").length,
  };

  const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.incomplete;
    const Icon = cfg.icon;
    const labelKey = status === "complete" ? "cooling.statusComplete"
      : status === "issue" ? "cooling.statusIssue" : "cooling.statusIncomplete";
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.color}`}>
        <Icon className="w-3 h-3" />
        {t(labelKey)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Wind className="w-6 h-6 text-primary" />
            {t("cooling.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t("cooling.subtitle")}</p>
        </div>
        <Button onClick={openAdd} data-testid="add-cooling-btn" className="bg-primary hover:bg-primary/90 text-white">
          <Plus className="w-4 h-4 mr-2" />
          {t("cooling.addEntry")}
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { key: "complete",   labelKey: "cooling.summary.complete",   dot: STATUS_CONFIG.complete.dot   },
          { key: "incomplete", labelKey: "cooling.summary.incomplete", dot: STATUS_CONFIG.incomplete.dot },
          { key: "issue",      labelKey: "cooling.summary.issue",      dot: STATUS_CONFIG.issue.dot      },
        ].map(({ key, labelKey, dot }) => (
          <button
            key={key}
            data-testid={`filter-${key}`}
            onClick={() => setFilterStatus(filterStatus === key ? "all" : key)}
            className={`rounded-xl border p-3 text-left transition-all hover:shadow-sm cursor-pointer ${filterStatus === key ? "ring-2 ring-primary" : "bg-white"}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className={`w-2 h-2 rounded-full ${dot}`} />
              <span className="text-xs text-muted-foreground font-medium">{t(labelKey)}</span>
            </div>
            <span className="text-2xl font-bold text-foreground">{counts[key]}</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div className="flex-1">
              <Label className="text-xs text-muted-foreground mb-1 block">{t("cooling.date")}</Label>
              <Input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="h-9"
                data-testid="filter-date-input"
              />
            </div>
            {filterDate && (
              <Button variant="ghost" size="sm" onClick={() => setFilterDate("")} className="text-muted-foreground">
                Limpiar filtro
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Records table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t("cooling.title")}</CardTitle>
          <CardDescription>{displayed.length} {displayed.length === 1 ? "registro" : "registros"}</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-10 text-muted-foreground text-sm">{t("common.loading")}</div>
          ) : displayed.length === 0 ? (
            <div className="text-center py-14 space-y-2" data-testid="no-cooling-records">
              <Wind className="w-10 h-10 text-muted-foreground/30 mx-auto" />
              <p className="text-sm font-medium text-muted-foreground">{t("cooling.noRecords")}</p>
              <p className="text-xs text-muted-foreground/70">{t("cooling.noRecordsHint")}</p>
              <Button size="sm" variant="outline" onClick={openAdd} className="mt-2">
                <Plus className="w-4 h-4 mr-1" />
                {t("cooling.addEntry")}
              </Button>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-xs text-muted-foreground">
                      <th className="text-left py-2 pr-4 font-medium">{t("cooling.product")}</th>
                      <th className="text-left py-2 pr-4 font-medium">{t("cooling.date")}</th>
                      <th className="text-left py-2 pr-4 font-medium">{t("cooling.entryTemp")} (C)</th>
                      <th className="text-left py-2 pr-4 font-medium">{t("cooling.entryTime")}</th>
                      <th className="text-left py-2 pr-4 font-medium">{t("cooling.exitTemp")} (C)</th>
                      <th className="text-left py-2 pr-4 font-medium">{t("cooling.exitTime")}</th>
                      <th className="text-left py-2 pr-4 font-medium">{t("cooling.status")}</th>
                      <th className="text-left py-2 font-medium">{t("cooling.actions")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/50">
                    {displayed.map((rec) => (
                      <tr key={rec.cooling_id} className="hover:bg-muted/30 transition-colors" data-testid={`cooling-row-${rec.cooling_id}`}>
                        <td className="py-2.5 pr-4 font-medium text-foreground">{rec.product_name}</td>
                        <td className="py-2.5 pr-4 text-muted-foreground">{rec.date}</td>
                        <td className="py-2.5 pr-4">{rec.entry_temperature}°C</td>
                        <td className="py-2.5 pr-4">{rec.entry_time}</td>
                        <td className="py-2.5 pr-4">{rec.exit_temperature != null ? `${rec.exit_temperature}°C` : <span className="text-muted-foreground/50">—</span>}</td>
                        <td className="py-2.5 pr-4">{rec.exit_time || <span className="text-muted-foreground/50">—</span>}</td>
                        <td className="py-2.5 pr-4"><StatusBadge status={rec.status} /></td>
                        <td className="py-2.5">
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => openEdit(rec)} data-testid={`edit-cooling-${rec.cooling_id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => setDeleteId(rec.cooling_id)} data-testid={`delete-cooling-${rec.cooling_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {displayed.map((rec) => (
                  <div key={rec.cooling_id} className="rounded-lg border bg-white p-3 space-y-2" data-testid={`cooling-card-${rec.cooling_id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-sm">{rec.product_name}</p>
                        <p className="text-xs text-muted-foreground">{rec.date}</p>
                      </div>
                      <StatusBadge status={rec.status} />
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-muted/40 rounded-md p-2">
                        <p className="text-muted-foreground font-medium mb-0.5">Entrada</p>
                        <p>{rec.entry_temperature}°C · {rec.entry_time}</p>
                      </div>
                      <div className="bg-muted/40 rounded-md p-2">
                        <p className="text-muted-foreground font-medium mb-0.5">Salida</p>
                        <p>{rec.exit_temperature != null ? `${rec.exit_temperature}°C` : "—"} · {rec.exit_time || "—"}</p>
                      </div>
                    </div>
                    {rec.notes && <p className="text-xs text-muted-foreground italic">{rec.notes}</p>}
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => openEdit(rec)}>
                        <Edit className="w-3 h-3 mr-1" /> {t("common.edit")}
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs flex-1 text-destructive hover:text-destructive" onClick={() => setDeleteId(rec.cooling_id)}>
                        <Trash2 className="w-3 h-3 mr-1" /> {t("common.delete")}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md" data-testid="cooling-form-dialog">
          <DialogHeader>
            <DialogTitle>{editRecord ? t("cooling.editTitle") : t("cooling.addTitle")}</DialogTitle>
            <DialogDescription>{t("cooling.subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs mb-1 block">{t("cooling.productNameLabel")}</Label>
              <Input
                placeholder={t("cooling.productNamePlaceholder")}
                value={form.product_name}
                onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                data-testid="cooling-product-input"
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">{t("cooling.dateLabel")}</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="cooling-date-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">{t("cooling.entryTempLabel")}</Label>
                <NumericInput
                  placeholder="65"
                  value={form.entry_temperature}
                  onChange={(e) => setForm({ ...form, entry_temperature: e.target.value })}
                  data-testid="cooling-entry-temp-input"
                />
              </div>
              <div>
                <Label className="text-xs mb-1 block">{t("cooling.entryTimeLabel")}</Label>
                <Input
                  type="time"
                  value={form.entry_time}
                  onChange={(e) => setForm({ ...form, entry_time: e.target.value })}
                  data-testid="cooling-entry-time-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">{t("cooling.exitTempLabel")}</Label>
                <NumericInput
                  placeholder="4  ó  -18"
                  value={form.exit_temperature}
                  onChange={(e) => setForm({ ...form, exit_temperature: e.target.value })}
                  data-testid="cooling-exit-temp-input"
                />
                <div className="mt-1.5">
                  <TempPresetButtons
                    onSelect={(v) => setForm({ ...form, exit_temperature: v })}
                    data-testid="cooling-exit-presets"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1 block">{t("cooling.exitTimeLabel")}</Label>
                <Input
                  type="time"
                  value={form.exit_time}
                  onChange={(e) => setForm({ ...form, exit_time: e.target.value })}
                  data-testid="cooling-exit-time-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">{t("cooling.notesLabel")}</Label>
              <Input
                placeholder={t("cooling.notesPlaceholder")}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="cooling-notes-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="cooling-save-btn" className="bg-primary text-white">
              {saving ? t("cooling.saving") : editRecord ? t("common.update") : t("common.add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent data-testid="cooling-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("cooling.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("cooling.deleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-white hover:bg-destructive/90">
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
